import { QRCodeSVG } from 'qrcode.react';
import { useEffect } from 'react';

import { t } from '@ping/helpers';
import { useCountdown, useMobile } from '@ping/hooks';
import { DateTime, getDuoNumber } from '@ping/utils';
import colors from '@ping/assets/scss/theme/exportVariables.module.scss';

import style from './style.module.scss';
import { Loading, Button, Spinner } from '@ping/uikit';

export {
  useWalletVerificationProgress,
  WALLET_VERIFICATION_PENDING_TOAST_ID,
} from './use-wallet-verification-progress.hook';

interface ILinkExternalWalletAddressProps {
  link?: string;
  defaultTime?: number;
  /**
   * CorePass has the request and is transferring the address. The QR code is covered and the
   * refresh is held back, so the same request cannot be scanned or reissued twice.
   */
  isProcessing?: boolean;
  onFinish?: () => void;
}

export function LinkExternalWalletAddress({
  link,
  defaultTime,
  isProcessing = false,
  onFinish,
}: ILinkExternalWalletAddressProps) {
  const timer = useCountdown(defaultTime || DateTime.milliseconds(0));
  const isMobile = useMobile();

  const isExpired = timer.minutes === 0 && timer.seconds === 0;

  useEffect(() => {
    /*
      Refreshing mid-transfer would issue a second QR code and a new connector reference key for a
      request the user has already accepted, so expiry is ignored while it is in flight.
    */
    if (isExpired && !isProcessing) {
      onFinish?.();
    }
  }, [isExpired, isProcessing]);

  return (
    <div className={style['link-external-wallet-address']} role='region'>
      <h4 className={style['link-external-wallet-address__title']}>
        {t('Link your external wallet address with Ping')}
      </h4>
      <p className={style['link-external-wallet-address__description']}>
        {isMobile
          ? t(
              `Tap the button below to open your CorePass app and share your newly verified wallet address with Ping. This step will enable future withdrawals from Ping to this address.`
            )
          : t(
              `Scan the QR code below with your CorePass app to share your newly verified wallet address with Ping. This step will enable future withdrawals from Ping to this address.`
            )}
      </p>

      <div className={style['link-external-wallet-address__box']}>
        <div className={style['link-external-wallet-address__refresh']}>
          {isProcessing ? (
            <span className={style['link-external-wallet-address__refresh-label']}>
              {t('Verification in progress')}
            </span>
          ) : (
            <>
              <span className={style['link-external-wallet-address__refresh-label']}>{t('QR code refresh in')}</span>
              &nbsp;
              <span className={style['link-external-wallet-address__refresh-timer']}>
                {getDuoNumber(timer.minutes)}:{getDuoNumber(timer.seconds)}
              </span>
            </>
          )}
        </div>
        <div
          className={!isMobile ? style['link-external-wallet-address__space'] : 'items-center'}
          aria-busy={isProcessing}
        >
          {(!isExpired || isProcessing) && (
            <>
              {isMobile ? (
                <div className={style['link-external-wallet-address__mobile-button']}>
                  <Button
                    type='primary'
                    size='large'
                    disabled={isProcessing}
                    onClick={() => window.open(decodeURIComponent(link), '_blank')}
                  >
                    {isProcessing ? t('Verification in progress') : t('Open in CorePass')}
                  </Button>
                </div>
              ) : (
                <QRCodeSVG
                  className={style['link-external-wallet-address__qr-code']}
                  size={304}
                  value={decodeURIComponent(link)}
                  level='L'
                />
              )}
            </>
          )}
          {isExpired && !isProcessing && (
            <div className={style['link-external-wallet-address__loading']}>
              <Loading />
            </div>
          )}
          {isProcessing && !isMobile && (
            <div className={style['link-external-wallet-address__processing']}>
              <Spinner spinnerType='clipLoader' color={colors.PrimaryLight} />
              <span className={style['link-external-wallet-address__processing-label']}>
                {t('Sharing your address with Ping...')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
